import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Monitor, Package, Laptop, RefreshCw, Wifi, Activity, ShieldCheck, AlertTriangle } from "lucide-react";

const TABS = [
  ["risk", "Endpoint Risk", AlertTriangle],
  ["software", "Software Inventory", Package],
  ["windows", "OS Inventory", Laptop],
  ["patch", "Patch Compliance", RefreshCw],
  ["internet", "Internet Stability", Wifi],
  ["health", "Device Health", Activity],
  ["warranty", "Warranty", ShieldCheck],
];

export default function EndpointIntelligence() {
  const [tab, setTab] = useState("risk");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/clients").then(r => setClients(r.data || [])); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = clientId ? { client_id: clientId } : {};
      const [software, risk, windows, patch, internet, health, warranty] = await Promise.all([
        api.get("/reports/software-inventory", { params }),
        api.get("/reports/endpoint-risk", { params }),
        api.get("/reports/os-inventory", { params }),
        api.get("/reports/patch-compliance", { params }),
        api.get("/reports/internet-stability", { params }),
        api.get("/reports/device-health", { params }),
        api.get("/reports/warranty", { params }),
      ]);
      setData({
        software: software.data,
        risk: risk.data,
        windows: windows.data,
        patch: patch.data,
        internet: internet.data,
        health: health.data,
        warranty: warranty.data,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clientId]);

  const active = data[tab] || {};

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Endpoint Intelligence</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Inventory & Compliance Reports</h1>
          <p className="text-sm text-zinc-500 mt-1">Software, Windows versions, patch posture, internet stability, health trends, and warranty tracking.</p>
        </div>

        <select value={clientId} onChange={e => setClientId(e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm">
          <option value="">Whole organization</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-6 gap-3">
        <KPI label="Endpoint Score" value={data.risk?.average_score || 0} tone={(data.risk?.average_score || 0) < 80 ? "warn" : ""} />
        <KPI label="Software Installs" value={data.software?.total_installs || 0} />
        <KPI label="OS Devices" value={data.windows?.total_devices || 0} />
        <KPI label="Patch Compliance" value={`${data.patch?.compliance_pct ?? 0}%`} />
        <KPI label="Internet Samples" value={data.internet?.total_samples || 0} />
        <KPI label="Health Devices" value={data.health?.devices || 0} />
        <KPI label="Warranty Unknown" value={data.warranty?.unknown || 0} tone={data.warranty?.unknown ? "warn" : ""} />
      </div>

      <div className="flex flex-wrap gap-2 border-b border-zinc-900">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-sm border-b-2 flex items-center gap-2 ${tab === k ? "border-blue-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {loading ? <div className="text-zinc-500 text-sm">Loading reports…</div> : null}

      {tab === "risk" && <RiskReport data={active} />}
      {tab === "software" && <SoftwareReport data={active} />}
      {tab === "windows" && <WindowsReport data={active} />}
      {tab === "patch" && <PatchReport data={active} />}
      {tab === "internet" && <InternetReport data={active} />}
      {tab === "health" && <HealthReport data={active} />}
      {tab === "warranty" && <WarrantyReport data={active} />}
    </div>
  );
}


function RiskReport({ data }) {
  return (
    <Panel title={`Endpoint Risk Score (${data.devices || 0} devices)`}>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 p-4 border-b border-zinc-900">
        <KPI label="Healthy" value={data.healthy || 0} />
        <KPI label="Warning" value={data.warning || 0} tone={(data.warning || 0) ? "warn" : ""} />
        <KPI label="Critical" value={data.critical || 0} tone={(data.critical || 0) ? "bad" : ""} />
        <KPI label="Average Score" value={data.average_score || 0} tone={(data.average_score || 0) < 80 ? "warn" : ""} />
      </div>
      <Table headers={["Device", "Score", "Level", "Risks", "CPU", "Memory", "Disk", "Patch", "Internet", "OS"]}>
        {(data.rows || []).map((r, i) => (
          <tr key={i} className="row">
            <td className="cell mono">{r.hostname}</td>
            <td className={`cell mono font-bold ${r.score < 60 ? "text-red-300" : r.score < 80 ? "text-amber-300" : "text-emerald-300"}`}>{r.score}</td>
            <td className="cell">{r.level}</td>
            <td className="cell text-xs">{(r.risks || []).join(", ") || "No major risk"}</td>
            <td className="cell mono">{r.cpu ?? "—"}%</td>
            <td className="cell mono">{r.mem ?? "—"}%</td>
            <td className="cell mono">{r.disk ?? "—"}%</td>
            <td className="cell">{r.patch_status || "unknown"}</td>
            <td className="cell mono">{r.internet_stability_pct == null ? "—" : `${r.internet_stability_pct}%`}</td>
            <td className="cell">{r.os_product || "—"} {r.os_build || ""}</td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

function SoftwareReport({ data }) {
  const [q, setQ] = useState("");
  const rows = useMemo(() => (data.rows || []).filter(r =>
    !q ||
    `${r.hostname} ${r.name} ${r.version} ${r.publisher}`.toLowerCase().includes(q.toLowerCase())
  ), [data, q]);

  return (
    <Panel title={`Software Inventory (${rows.length})`}>
      <div className="p-4 border-b border-zinc-900">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search software, device, publisher..." className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
      </div>
      <Table headers={["Device", "User", "Software", "Version", "Publisher", "Install Date"]}>
        {rows.slice(0, 1000).map((r, i) => (
          <tr key={i} className="row">
            <td className="cell mono">{r.hostname}</td>
            <td className="cell">{r.current_user || "—"}</td>
            <td className="cell">{r.name}</td>
            <td className="cell mono">{r.version || "—"}</td>
            <td className="cell">{r.publisher || "—"}</td>
            <td className="cell mono">{r.install_date || "—"}</td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

function WindowsReport({ data }) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <Panel title="OS Version Summary">
        <Table headers={["Platform", "Product", "Version", "Build", "Devices"]}>
          {(data.versions || []).map((r, i) => (
            <tr key={i} className="row">
              <td className="cell mono">{r.platform}</td>
              <td className="cell">{r.os_product}</td>
              <td className="cell mono">{r.os_version}</td>
              <td className="cell mono">{r.os_build}</td>
              <td className="cell mono">{r.devices}</td>
            </tr>
          ))}
        </Table>
      </Panel>
      <Panel title="Device Details">
        <Table headers={["Device", "Platform", "User", "Product", "Version", "Build", "Captured"]}>
          {(data.rows || []).map((r, i) => (
            <tr key={i} className="row">
              <td className="cell mono">{r.hostname}</td>
              <td className="cell mono">{r.platform || "—"}</td>
              <td className="cell">{r.current_user || "—"}</td>
              <td className="cell">{r.os_product || "—"}</td>
              <td className="cell mono">{r.os_version || "—"}</td>
              <td className="cell mono">{r.os_build || "—"}</td>
              <td className="cell mono">{r.captured_at?.slice(0, 19) || "—"}</td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
}

function PatchReport({ data }) {
  return (
    <Panel title={`Patch Compliance (${data.compliance_pct ?? 0}%)`}>
      <Table headers={["Device", "User", "Patch Status", "Pending", "Last Hotfix", "Captured"]}>
        {(data.rows || []).map((r, i) => (
          <tr key={i} className="row">
            <td className="cell mono">{r.hostname}</td>
            <td className="cell">{r.current_user || "—"}</td>
            <td className="cell">{r.patch_status || "unknown"}</td>
            <td className="cell mono">{r.pending_updates ?? "—"}</td>
            <td className="cell mono">{r.last_hotfix?.HotFixID || "—"}</td>
            <td className="cell mono">{r.captured_at?.slice(0, 19) || "—"}</td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

function InternetReport({ data }) {
  return (
    <Panel title="Internet Stability">
      <Table headers={["Device", "Samples", "Unstable Samples", "Stability", "Client"]}>
        {(data.rows || []).map((r, i) => (
          <tr key={i} className="row">
            <td className="cell mono">{r.hostname}</td>
            <td className="cell mono">{r.samples}</td>
            <td className="cell mono">{r.unstable_samples}</td>
            <td className={`cell mono ${r.stability_pct < 95 ? "text-amber-300" : "text-emerald-300"}`}>{r.stability_pct}%</td>
            <td className="cell mono">{r.client_id || "—"}</td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

function HealthReport({ data }) {
  return (
    <Panel title="Device Health Latest Snapshot">
      <Table headers={["Device", "CPU", "Memory", "Disk", "Uptime", "Captured"]}>
        {(data.rows || []).map((r, i) => (
          <tr key={i} className="row">
            <td className="cell mono">{r.hostname}</td>
            <td className={`cell mono ${(r.cpu || 0) >= 85 ? "text-red-300" : ""}`}>{r.cpu ?? "—"}%</td>
            <td className={`cell mono ${(r.mem || 0) >= 85 ? "text-red-300" : ""}`}>{r.mem ?? "—"}%</td>
            <td className={`cell mono ${(r.disk || 0) >= 85 ? "text-red-300" : ""}`}>{r.disk ?? "—"}%</td>
            <td className="cell mono">{Math.round((r.uptime_sec || 0) / 3600)}h</td>
            <td className="cell mono">{r.captured_at?.slice(0, 19) || "—"}</td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

function WarrantyReport({ data }) {
  return (
    <Panel title="Warranty Tracking">
      <Table headers={["Device", "Manufacturer", "Model", "Serial", "Warranty Status", "Expiry"]}>
        {(data.rows || []).map((r, i) => (
          <tr key={i} className="row">
            <td className="cell mono">{r.hostname}</td>
            <td className="cell">{r.manufacturer || "—"}</td>
            <td className="cell">{r.model || "—"}</td>
            <td className="cell mono">{r.serial_number || "—"}</td>
            <td className="cell">{r.warranty_status || "unknown"}</td>
            <td className="cell mono">{r.warranty_expiry || "—"}</td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

function KPI({ label, value, tone }) {
  const color = tone === "bad" ? "text-red-300" : tone === "warn" ? "text-amber-300" : "text-white";
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className={`font-mono text-2xl font-bold mt-2 ${color}`}>{value}</div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-900 font-display font-medium">{title}</div>
      {children}
    </div>
  );
}

function Table({ headers, children }) {
  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          <tr>{headers.map(h => <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
